/**
 * Tanjong Life · the canvas helpers every build scenario shares.
 *
 * Two ways a process reaches the open canvas here:
 *   · `slicer(doc)` — cut an AUTHORED `ProcessDoc` (the pr12/pr15 assets) into the
 *     increments a conversation streams. Each slice carries the connections whose both
 *     endpoints have now appeared, so edges materialise exactly when they can render.
 *   · `outlineDoc(...)` — assemble a compact, hand-authored outline into a doc with the
 *     same geometry the authored ones use. The three processes already on the shelf
 *     (tl-pr11/13/14) are narrated from an outline; the scenario never deploys them.
 *
 * Trigger ids and facts mirror `scripts/tanjong/registry.mjs` (the scenario chunk cannot
 * import the registry); `tests/tanjong-scenarios.test.ts` asserts the mirror is exact.
 */
import type {
  ActivityNode,
  ActivityType,
  Connection,
  Milestone,
  ProcessAgentIdentity,
  ProcessDoc,
  Stage,
  Trigger,
} from '@wand/shared';
import type { WizardCanvasPatch } from '../../gateway/wizard.js';

/* ------------------------------------------------------------ triggers (registry mirror) */

export type TlTriggerId =
  | 'trg-m365-mail-received'
  | 'trg-fax-gateway'
  | 'trg-claims-efiling-submitted'
  | 'trg-claims-adjudicated'
  | 'trg-claims-in-assessment'
  | 'trg-disbursements-bank-return'
  | 'trg-policy-mandate-cancelled'
  | 'trg-policy-application-received'
  | 'trg-whatsapp-message'
  | 'trg-lapse-sweep';

interface TlTrigger {
  connectorId: string;
  name: string;
  event: string;
  source: string;
}

/** `registry.TRIGGERS`, the four fields a canvas trigger needs. */
export const TL_TRIGGERS: Record<TlTriggerId, TlTrigger> = {
  'trg-m365-mail-received': {
    connectorId: 'm365',
    name: 'Claims intake mailbox · mail received',
    event: 'Email received',
    source: 'webhook',
  },
  'trg-fax-gateway': {
    connectorId: 'm365',
    name: 'Fax gateway · page images arrive',
    event: 'Document received',
    source: 'file',
  },
  'trg-claims-efiling-submitted': {
    connectorId: 'claimscore',
    name: 'Hospital e-filing · claim submitted',
    event: 'Claim submitted',
    source: 'webhook',
  },
  'trg-claims-adjudicated': {
    connectorId: 'claimscore',
    name: 'Claim adjudicated',
    event: 'Claim adjudicated',
    source: 'webhook',
  },
  'trg-claims-in-assessment': {
    connectorId: 'claimscore',
    name: 'Claim enters assessment',
    event: 'Claim in assessment',
    source: 'queue',
  },
  'trg-disbursements-bank-return': {
    connectorId: 'disbursements',
    name: 'Bank return file · GIRO returns',
    event: 'Bank return received',
    source: 'file',
  },
  'trg-policy-mandate-cancelled': {
    connectorId: 'policyledger',
    name: 'GIRO mandate cancelled',
    event: 'Mandate cancelled',
    source: 'change_feed',
  },
  'trg-policy-application-received': {
    connectorId: 'policyledger',
    name: 'New business application received',
    event: 'Application received',
    source: 'webhook',
  },
  'trg-whatsapp-message': {
    connectorId: 'whatsapp',
    name: 'Policyholder message received',
    event: 'Message posted',
    source: 'webhook',
  },
  'trg-lapse-sweep': {
    connectorId: 'policyledger',
    name: 'Nightly at-risk sweep',
    event: 'Premium overdue',
    source: 'schedule',
  },
};

/** A registry trigger as a canvas trigger, placed in the trigger column. */
export function tlTrigger(id: TlTriggerId, index: number, desc: string): Trigger {
  const t = TL_TRIGGERS[id];
  return {
    id,
    kind: t.source === 'schedule' ? 'timer' : 'app',
    app: t.connectorId,
    event: t.event,
    name: t.name,
    desc,
    x: 90,
    y: 190 + index * 110,
  };
}

/* ------------------------------------------------------------------------- slicing */

export interface Slicer {
  /** One increment: the elements named plus every connection both of whose ends are now on the canvas. */
  slice(p: Omit<WizardCanvasPatch, 'conns'>): WizardCanvasPatch;
  stages: Stage[];
  nodesIn(stageId: string, omit?: string[]): ActivityNode[];
  /** Milestones whose incoming connection leaves this stage. */
  milestonesOf(stageId: string): Milestone[];
  /** The stage holding the first node of the given type (the gate lives in the `human` one). */
  stageWith(type: ActivityType): Stage | undefined;
  /** The i-th stage (0-based); throws on an outline that does not have it, so an authoring
   *  slip fails at module load rather than as an empty canvas on stage. */
  stageAt(i: number): Stage;
  /** The i-th stage as one slice: the stage, its nodes and the milestones it reaches. */
  stagePatch(i: number, omit?: string[]): WizardCanvasPatch;
  /** Every stage as one ordered slice; process-level fields ride with the last one. */
  perStage(): WizardCanvasPatch[];
}

/** Doc fields may be absent while an asset is still a placeholder — read them defensively. */
const list = <T>(v: T[] | undefined): T[] => (Array.isArray(v) ? v : []);

export function slicer(doc: ProcessDoc): Slicer {
  const seen = new Set<string>();
  const usedConns = new Set<string>();
  const conns = list(doc.conns);
  const nodes = list(doc.nodes);
  const stages = list(doc.stages);
  const milestones = list(doc.milestones);

  const slice = (p: Omit<WizardCanvasPatch, 'conns'>): WizardCanvasPatch => {
    for (const el of [
      ...(p.triggers ?? []),
      ...(p.stages ?? []),
      ...(p.nodes ?? []),
      ...(p.milestones ?? []),
    ])
      seen.add(el.id);
    const ready: Connection[] = conns.filter(
      (c) => !usedConns.has(c.id) && seen.has(c.fromId) && seen.has(c.toId),
    );
    for (const c of ready) usedConns.add(c.id);
    return { ...p, conns: ready };
  };
  const nodesIn = (sid: string, omit: string[] = []) =>
    nodes.filter((n) => n.stageId === sid && !omit.includes(n.id));
  const milestonesOf = (sid: string) =>
    milestones.filter((m) =>
      conns.some((c) => c.toKind === 'milestone' && c.toId === m.id && c.fromId === sid),
    );
  const stageWith = (type: ActivityType) => {
    const n = nodes.find((x) => x.type === type);
    return stages.find((s) => s.id === n?.stageId);
  };
  const stageAt = (i: number): Stage => {
    const s = stages[i];
    if (!s) throw new Error(`${doc.id}: no stage at index ${i}`);
    return s;
  };
  const stagePatch = (i: number, omit: string[] = []) => {
    const s = stageAt(i);
    return slice({ stages: [s], nodes: nodesIn(s.id, omit), milestones: milestonesOf(s.id) });
  };
  const perStage = () =>
    stages.map((s, i) =>
      slice({
        stages: [s],
        nodes: nodesIn(s.id),
        milestones: milestonesOf(s.id),
        ...(i === stages.length - 1
          ? { objective: doc.objective ?? null, metrics: doc.metrics ?? [] }
          : {}),
      }),
    );
  return { slice, stages, nodesIn, milestonesOf, stageWith, stageAt, stagePatch, perStage };
}

/** Lazy, memoised, order-preserving access to one named increment. */
export function lazyPatches<K extends string>(
  build: () => Promise<Record<K, WizardCanvasPatch>>,
): (k: K) => () => Promise<WizardCanvasPatch> {
  let patches: Promise<Record<K, WizardCanvasPatch>> | null = null;
  return (k) => async () => {
    if (!patches) patches = build();
    return (await patches)[k];
  };
}

/* ------------------------------------------------------------------- outlines */

export interface OutlineStep {
  name: string;
  type: ActivityType;
  desc: string;
  /** App key for `application` steps. */
  app?: string;
  /** Group performer (a gate) or the agent invoked. */
  performer?: { kind: 'group' | 'agent'; label: string };
  sla?: { amount: string; unit: string };
  /** `left = true` guard, as one variable name. */
  guard?: string;
}
export interface OutlineStage {
  name: string;
  color: 'teal' | 'blue' | 'amber' | 'red' | 'pink' | 'green';
  steps: OutlineStep[];
  /** A milestone reached on leaving this stage. */
  milestone?: { name: string; desc: string; guard: string };
}
export interface Outline {
  pk: string;
  name: string;
  agent: ProcessAgentIdentity;
  triggers: Trigger[];
  /** Trigger id → 0-based stage index it enters (default: the first stage). */
  triggerEntry?: Record<string, number>;
  stages: OutlineStage[];
  objective?: string | null;
  metrics?: string[];
}

const STAGE_X0 = 360;
const STAGE_GAP = 280;

/** Lay the outline out with the geometry the authored processes use. */
export function outlineDoc(o: Outline): ProcessDoc {
  const stages: Stage[] = [];
  const nodes: ActivityNode[] = [];
  const milestones: Milestone[] = [];
  const conns: Connection[] = [];
  let a = 0;
  const conn = (
    fromKind: Connection['fromKind'],
    fromId: string,
    toKind: Connection['toKind'],
    toId: string,
  ) => conns.push({ id: `${o.pk}-c${conns.length + 1}`, fromKind, fromId, toKind, toId });

  o.stages.forEach((st, i) => {
    const sid = `${o.pk}-s${i + 1}`;
    const x = STAGE_X0 + i * STAGE_GAP;
    stages.push({
      id: sid,
      name: st.name,
      x,
      y: 110,
      w: 260,
      h: 120 + st.steps.length * 78,
      color: st.color,
      after: i === 0 ? '' : `${o.pk}-s${i}`,
      guards: [],
    });
    st.steps.forEach((step, j) => {
      a += 1;
      const id = `${o.pk}-a${a}`;
      nodes.push({
        id,
        type: step.type,
        name: step.name,
        desc: step.desc,
        x: x + 18,
        y: 180 + j * 78,
        stageId: sid,
        ...(step.app ? { app: step.app } : {}),
        ...(step.performer ? { performer: step.performer } : {}),
        ...(step.sla ? { sla: step.sla } : {}),
        ...(step.guard
          ? { guards: [{ id: `${id}-g1`, left: step.guard, op: '=', right: 'true' }] }
          : {}),
        ...(i === 0 && j === 0 ? { entry: true } : {}),
      });
    });
    for (const t of o.triggers)
      if ((o.triggerEntry?.[t.id] ?? 0) === i) conn('trigger', t.id, 'stage', sid);
    if (i > 0) conn('stage', `${o.pk}-s${i}`, 'stage', sid);
    if (st.milestone) {
      const mid = `${o.pk}-m${milestones.length + 1}`;
      milestones.push({
        id: mid,
        name: st.milestone.name,
        desc: st.milestone.desc,
        x: x + 60,
        y: 620,
        guards: [{ id: `${mid}-g1`, left: st.milestone.guard, op: '=', right: 'true' }],
      });
      conn('stage', sid, 'milestone', mid);
    }
  });

  return {
    id: o.pk,
    name: o.name,
    agent: o.agent,
    nodes,
    stages,
    triggers: o.triggers,
    milestones,
    conns,
    vars: [],
    objective: o.objective ?? null,
    metrics: o.metrics ?? [],
    scope: 'internal',
    departments: [],
    updatedAt: '2026-08-24T02:00:00Z',
  };
}
